/**
 * Algolia Multilingual Utilities
 * Centralized language detection and index mapping for Algolia search
 */

class AlgoliaUtils {
  /**
   * Detect language and market from the current URL path
   * @returns {Object} Object containing language and market
   */
  static getLanguageAndMarket() {
    const path = window.location.pathname;
    const hostname = window.location.hostname;
    
    const isEnglish = path.startsWith('/en') || path.includes('/en/');
    const language = isEnglish ? 'en' : 'de';
    
    let market = 'deutschland';
    
    if (hostname.includes('swiss') || hostname.includes('ch')) {
      market = 'swiss';
    }
    
    if (path.includes('/swiss/') || path.includes('/ch/')) {
      market = 'swiss';
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    const shopifyMarket = urlParams.get('market');
    if (shopifyMarket) {
      switch (shopifyMarket.toLowerCase()) {
        case 'swiss':
        case 'ch':
          market = 'swiss';
          break;
        case 'deutschland':
        case 'de':
        case 'germany':
          market = 'deutschland';
          break;
      }
    }
    
    return { language, market };
  }

  /**
   * Get the correct Algolia index name based on detected language and market
   * @param {string} baseIndex - The base index name (e.g., 'shopify_products')
   * @returns {string} The language-specific index name
   */
  static getIndexName(baseIndex) {
    const { language: detectedLanguage, market: detectedMarket } = this.getLanguageAndMarket();
    
    const indexSuffixes = {
      'deutschland_de': 'shopify_products',
      'deutschland_en': 'shopify_products_deutschland_en',
      'eu_zypern_de': 'shopify_products_eu_zypern_de',
      'eu_zypern_en': 'shopify_products_eu_zypern_en',
      'swiss_de': 'shopify_products_swiss_de',
      'swiss_en': 'shopify_products_swiss_en',
    };
    
    const key = `${detectedMarket}_${detectedLanguage}`;
    return indexSuffixes[key] || baseIndex;
  }

  /**
   * Get language-specific URL prefix
   * @returns {string} URL prefix for the current language (e.g., '/en' for English)
   */
  static getLanguagePrefix() {
    const { language } = this.getLanguageAndMarket();
    return language === 'en' ? '/en' : '';
  }

  /**
   * Get translations for UI text based on detected language
   * @param {string} key - Translation key
   * @returns {string} Translated text
   */
  static getTranslation(key) {
    const { language } = this.getLanguageAndMarket();
    const translations = {
      en: {
        products: 'PRODUCTS',
        show_all_products: 'SHOW ALL PRODUCTS',
        recent_searches: 'RECENT SEARCHES',
        suggestions: 'SUGGESTIONS',
        placeholder: 'Search',
        no_results: 'No results found',
        variants: 'VARIANTS',
      },
      de: {
        products: 'PRODUKTE',
        show_all_products: 'ALLE PRODUKTE ANZEIGEN',
        recent_searches: 'ZULETZT GESUCHT',
        suggestions: 'VORSCHLÄGE',
        placeholder: 'Suchen',
        no_results: 'Keine Ergebnisse gefunden',
        variants: 'Varianten',
      },
    };

    return translations[language] ? translations[language][key] : translations.de[key];
  }

  /**
   * Get sort index name for price sorting
   * @param {string} sortValue - Sort value (e.g., 'price-ascending')
   * @returns {string} The sort-specific index name
   */
  static getSortIndexName(sortValue) {
    const baseIndex = this.getIndexName('shopify_products');
    const indexMap = {
      'price-ascending': `${baseIndex}_price_asc`,
      'price-descending': `${baseIndex}_price_desc`,
    };

    return indexMap[sortValue] || baseIndex;
  }
}

window.AlgoliaUtils = AlgoliaUtils;